import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Rating,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { RootState, AppDispatch } from '../../app/store';
import { Post } from '../../types';
import { fetchSentimentAnalytics } from '../../features/analytics/analyticsSlice';
import { fetchPlaces, fetchAllPlacesAnalytics } from '../../features/googleReviews/googleReviewsSlice';
import { getPosts } from '../../features/feedback/feedbackSlice';
import api from '../../config/api';

// Define brand interface
interface Brand {
  brand_id: string;
  brand_name: string;
  platform?: string;
}

// Define Google stats interface
interface GoogleStats {
  averageRating: number;
  totalReviews: number;
}

const StatCard: React.FC<{
  title: string;
  value: number | string;
  description?: string;
  rating?: number;
  loading?: boolean;
}> = ({ title, value, description, rating, loading }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80 }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography color="textSecondary" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="div">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {rating !== undefined && (
            <Rating value={rating} precision={0.1} readOnly size="small" />
          )}
        </Box>
        {description && (
          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const OverviewPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { posts, isLoading: isPostsLoading } = useSelector((state: RootState) => state.feedback);
  const { sentimentData, loading: isAnalyticsLoading } = useSelector((state: RootState) => state.analytics);
  const { places, placeAnalytics, loading: isGoogleLoading } = useSelector((state: RootState) => state.googleReviews);
  const theme = useTheme();
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState<boolean>(false);
  const [googleStats, setGoogleStats] = useState<GoogleStats>({ averageRating: 0, totalReviews: 0 });
  const [isLoadingGoogleStats, setIsLoadingGoogleStats] = useState<boolean>(false);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);

  // Set initial date to current year
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const currentDate = new Date();
    return `${currentDate.getFullYear()}-01`; // YYYY-MM format for current year, January
  });

  // Generate list of months (current month and past 11 months)
  const monthOptions = React.useMemo(() => {
    const options = [];
    const today = new Date();
    
    // Add "Current Year" option
    options.push({
      value: `${today.getFullYear()}-01`,
      label: today.getFullYear().toString()
    });

    // Add monthly options
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      // Format date as YYYY-MM using local date components to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const value = `${year}-${month}`;
      const label = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  // Get date range for the selected month/year
  const getDateRange = React.useCallback((monthString: string) => {
    if (!monthString) {
      return {
        startDate: '1970-01-01',
        endDate: new Date().toISOString()
      };
    }
    
    const [year, month] = monthString.split('-').map(Number);
    
    // If only year is selected (month is 1), get full year range
    if (month === 1 && monthString === `${new Date().getFullYear()}-01`) {
      const startDate = new Date(year, 0, 1); // January 1st
      const endDate = new Date(); // Current date for current year
      endDate.setHours(23, 59, 59, 999);
      
      return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };
    }
    
    // Regular month range
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    endDate.setHours(23, 59, 59, 999);
    
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  }, []);

  // Fetch available brands
  useEffect(() => {
    const fetchBrands = async () => {
      setLoadingBrands(true);
      try {
        const response = await api.get('/feedback/brands');
        
        // Process to get unique brands
        const uniqueBrands = Array.from(
          new Map(
            response.data.data
              .filter((item: any) => item.brand_id && item.brand_name)
              .map((item: any) => [item.brand_id, { brand_id: item.brand_id, brand_name: item.brand_name }])
          ).values()
        );
        
        setBrands(uniqueBrands as Brand[]);
        setLoadingBrands(false);
      } catch (error) {
        console.error('Error fetching brands:', error);
        setLoadingBrands(false);
      }
    };
    
    fetchBrands();
  }, []);

  // Calculate sentiment data
  const { instagramSentiment, googleSentiment, tiktokSentiment, gccSentiment } = React.useMemo(() => {
    // Calculate Instagram sentiment percentages
    const instagramSentiment = Array.isArray(sentimentData) ? sentimentData
      .filter(item => item.source === 'instagram')
      .reduce(
        (acc, item) => {
          const count = Number(item.count);
          acc.total += count;
          if (item.sentiment && item.sentiment.toLowerCase() === 'positive') acc.positive += count;
          if (item.sentiment && item.sentiment.toLowerCase() === 'negative') acc.negative += count;
          if (item.sentiment && item.sentiment.toLowerCase() === 'neutral') acc.neutral += count;
          return acc;
        },
        { total: 0, positive: 0, negative: 0, neutral: 0 }
      ) : { total: 0, positive: 0, negative: 0, neutral: 0 };
    
    // Calculate Google sentiment percentages
    const googleSentiment = Array.isArray(sentimentData) ? sentimentData
      .filter(item => item.source === 'google')
      .reduce(
        (acc, item) => {
          const count = Number(item.count);
          acc.total += count;
          if (item.sentiment && item.sentiment.toLowerCase() === 'positive') acc.positive += count;
          if (item.sentiment && item.sentiment.toLowerCase() === 'negative') acc.negative += count;
          if (item.sentiment && item.sentiment.toLowerCase() === 'neutral') acc.neutral += count;
          return acc;
        },
        { total: 0, positive: 0, negative: 0, neutral: 0 }
      ) : { total: 0, positive: 0, negative: 0, neutral: 0 };

    // Calculate TikTok sentiment percentages
    const tiktokSentiment = Array.isArray(sentimentData) ? sentimentData
      .filter(item => item.source === 'tiktok')
      .reduce(
        (acc, item) => {
          const count = Number(item.count);
          acc.total += count;
          if (item.sentiment && item.sentiment.toLowerCase() === 'positive') acc.positive += count;
          if (item.sentiment && item.sentiment.toLowerCase() === 'negative') acc.negative += count;
          if (item.sentiment && item.sentiment.toLowerCase() === 'neutral') acc.neutral += count;
          return acc;
        },
        { total: 0, positive: 0, negative: 0, neutral: 0 }
      ) : { total: 0, positive: 0, negative: 0, neutral: 0 };

    // Calculate GCC sentiment percentages
    const gccSentiment = Array.isArray(sentimentData) ? sentimentData
      .filter(item => item.source === 'gcc')
      .reduce(
        (acc, item) => {
          const count = Number(item.count);
          if (item.sentiment === 'positive') {
            acc.positive = count;
            acc.total += count;
          } else if (item.sentiment === 'negative') {
            acc.negative = count;
            acc.total += count;
          }
          return acc;
        },
        { total: 0, positive: 0, negative: 0, neutral: 0 }
      ) : { total: 0, positive: 0, negative: 0, neutral: 0 };

    return { instagramSentiment, googleSentiment, tiktokSentiment, gccSentiment };
  }, [sentimentData]);

  const stats = React.useMemo(() => {
    const totalLikes = posts.reduce((sum, post) => sum + (post.likes_count || 0), 0);
    const totalComments = instagramSentiment.total + tiktokSentiment.total;
    
    const instagramScores = instagramSentiment.total > 0 ? {
      positive: Math.round((instagramSentiment.positive / instagramSentiment.total) * 100).toString(),
      neutral: Math.round((instagramSentiment.neutral / instagramSentiment.total) * 100).toString(),
      negative: Math.round((instagramSentiment.negative / instagramSentiment.total) * 100).toString()
    } : { positive: '0', neutral: '0', negative: '0' };
    
    const googleScores = googleSentiment.total > 0 ? {
      positive: Math.round((googleSentiment.positive / googleSentiment.total) * 100).toString(),
      neutral: Math.round((googleSentiment.neutral / googleSentiment.total) * 100).toString(),
      negative: Math.round((googleSentiment.negative / googleSentiment.total) * 100).toString()
    } : { positive: '0', neutral: '0', negative: '0' };

    const tiktokScores = tiktokSentiment.total > 0 ? {
      positive: Math.round((tiktokSentiment.positive / tiktokSentiment.total) * 100).toString(),
      neutral: Math.round((tiktokSentiment.neutral / tiktokSentiment.total) * 100).toString(),
      negative: Math.round((tiktokSentiment.negative / tiktokSentiment.total) * 100).toString()
    } : { positive: '0', neutral: '0', negative: '0' };

    const gccScores = gccSentiment.total > 0 ? {
      positive: Math.round((gccSentiment.positive / gccSentiment.total) * 100).toString(),
      negative: Math.round((gccSentiment.negative / gccSentiment.total) * 100).toString()
    } : { positive: '0', negative: '0' };

    return {
      googleRating: googleStats.averageRating,
      totalReviews: googleStats.totalReviews,
      instagramSentiment: `${instagramScores.positive}% 😊 ${instagramScores.neutral}% 😐 ${instagramScores.negative}% 😞`,
      googleSentiment: `${googleScores.positive}% 😊 ${googleScores.neutral}% 😐 ${googleScores.negative}% 😞`,
      tiktokSentiment: `${tiktokScores.positive}% 😊 ${tiktokScores.neutral}% 😐 ${tiktokScores.negative}% 😞`,
      gccSentiment: `${gccScores.positive}% 😊 ${gccScores.negative}% 😞`,
    };
  }, [posts, instagramSentiment, googleSentiment, tiktokSentiment, gccSentiment, googleStats]);

  // Function to fetch Google stats
  const fetchGoogleStats = async (dateRange: { startDate: string; endDate: string }) => {
    setIsLoadingGoogleStats(true);
    try {
      const response = await api.get('/google-reviews/places-analytics', {
        params: {
          ...(selectedBrand ? { brand_id: selectedBrand } : {}),
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });
      
      if (response.data && response.data.overall_stats) {
        setGoogleStats({
          averageRating: response.data.overall_stats.average_rating || 0,
          totalReviews: response.data.overall_stats.total_reviews || 0
        });
      }
    } catch (error) {
      console.error('Error in direct Google stats fetch:', error);
    } finally {
      setIsLoadingGoogleStats(false);
    }
  };

  // Effect for when selected brand or month changes
  useEffect(() => {
    const fetchData = async () => {
      setIsFiltering(true);
      try {
        const dateRange = getDateRange(selectedMonth);
        
        // Fetch all data concurrently
        await Promise.all([
          dispatch(getPosts({ 
            brand_id: selectedBrand,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
          })),
          dispatch(fetchSentimentAnalytics({ 
            startDate: dateRange.startDate, 
            endDate: dateRange.endDate,
            brand_id: selectedBrand
          })),
          dispatch(fetchPlaces(selectedBrand)),
          fetchGoogleStats(dateRange)
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsFiltering(false);
      }
    };

    fetchData();
  }, [dispatch, selectedBrand, selectedMonth, getDateRange]);

  // Combined loading state
  const isLoading = isPostsLoading || 
                   isAnalyticsLoading || 
                   isGoogleLoading || 
                   loadingBrands || 
                   isLoadingGoogleStats ||
                   isFiltering;

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Overview
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl variant="outlined" sx={{ minWidth: 200 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              label="Month"
              disabled={isLoading}
            >
              <MenuItem value="">All Time</MenuItem>
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" sx={{ minWidth: 200 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              label="Brand"
              disabled={isLoading}
            >
              <MenuItem value="">All Brands</MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand.brand_id} value={brand.brand_id}>
                  {brand.brand_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Instagram Sentiment"
            value={stats.instagramSentiment}
            description={`${instagramSentiment.negative.toLocaleString()} negative comments`}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Google Rating"
            value={stats.googleRating.toFixed(1)}
            description={`Based on ${stats.totalReviews.toLocaleString()} reviews`}
            rating={stats.googleRating}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="TikTok Sentiment"
            value={stats.tiktokSentiment}
            description={`${tiktokSentiment.negative.toLocaleString()} negative comments`}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Guest Comment Cards"
            value={stats.gccSentiment}
            description={`${gccSentiment.total.toLocaleString()} responses (${gccSentiment.negative.toLocaleString()} poor feedback)`}
            loading={isLoading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverviewPage; 